import React from 'react';

/**
 * How long to keep re-applying a restored offset before giving up. A listing
 * renders its rows asynchronously, so the page is usually too short to hold the
 * offset on the first frame — and a listing that has to fetch its rows can take
 * far longer than a few frames. Two seconds covers a cold fetch without leaving
 * the page twitching; a user who starts scrolling ends it sooner.
 */
const RESTORE_BUDGET_IN_MS = 2000;

/** Gestures that mean the reader has taken over and we should stop scrolling. */
const USER_TOOK_OVER_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

interface Params {
  /** Re-position the window whenever this changes — normally the pathname. */
  watchValue: any;
  /** Offset to return to, when navigating back to a listing we left earlier. */
  restoreTo?: number;
}

/**
 * Positions the window on navigation: back to the top for a new screen, or back
 * to where the underwriter left a listing when they are returning to it.
 */
export const useNavigationScroll = ({ watchValue, restoreTo }: Params) => {
  // Read through a ref so only `watchValue` drives this. `restoreTo` comes from
  // router state, which QueryParamsProvider rewrites on every filter, sort and
  // page change — depending on it would re-run the effect mid-listing and snap
  // the reader back to the top.
  const restoreToRef = React.useRef(restoreTo);
  restoreToRef.current = restoreTo;

  React.useLayoutEffect(() => {
    const offset = restoreToRef.current;

    if (!offset) {
      window.scrollTo(0, 0);
      return;
    }

    const giveUpAt = Date.now() + RESTORE_BUDGET_IN_MS;
    let pendingFrame = 0;
    let userTookOver = false;

    const stop = () => {
      userTookOver = true;
      window.cancelAnimationFrame(pendingFrame);
    };

    const applyOffset = () => {
      if (userTookOver) {
        return;
      }

      window.scrollTo(0, offset);

      // A page too short to hold the offset has not finished rendering its rows.
      if (window.scrollY !== offset && Date.now() < giveUpAt) {
        pendingFrame = window.requestAnimationFrame(applyOffset);
      }
    };

    USER_TOOK_OVER_EVENTS.forEach((event) =>
      window.addEventListener(event, stop, { passive: true })
    );
    applyOffset();

    return () => {
      window.cancelAnimationFrame(pendingFrame);
      USER_TOOK_OVER_EVENTS.forEach((event) =>
        window.removeEventListener(event, stop)
      );
    };
  }, [watchValue]);
};
